import {
  Extract_PropertyOperation_MeliPublicationDraftOperation_,
  PropertyMediaKind,
  PropertyOperation,
  PropertyPortal,
  PropertyPortalPublicationConnectionState,
  PropertyPortalPublicationRecordMode,
  PropertyPortalPublicationStatus,
  type PrepareMeliPublicationDraftParams,
  type PropertyPortalPublication,
  type CatalogProperty,
} from 'src/api/generated';
import type { ComponentVariant } from 'src/modules/shared/types/component.type';
import { formatDate } from 'src/modules/shared/utils/formatting/date.util';
import type { PropertyPublicationListItem } from '../components/molecules/property-publication-list';
import type { PropertyPublicationActionItem } from '../types/property-publication-actions.type';
import type {
  BuildPropertyPublicationListItemsParams,
  QueuedPublicationAction,
} from '../types/property-publishing-panel.type';

import {
  canPublishMeliPublication,
  getCurrentMeliPublication,
  getMeliDisplayStatus,
  getMeliPublicationConfigString,
  getMeliPublicationConfigStringArray,
  sortMeliPublicationsByRecency,
  getSupportedMeliOperationFromPublication,
  type SupportedMeliOperation,
} from './meli-publication.util';
import {
  getPropertyPublicationProviderBrandId,
  getPropertyPublicationProviderLabelKey,
  getPropertyPublicationStatusDisplay,
} from './property-publication-display.util';
import { parsePropertyCoordinate } from './property-location.util';
import { getPublicationPortalTarget } from './property-meli-publishing-manager.util';
import { getPropertyMediaFileIdsByKinds } from './property-media.util';
import { resolvePublicationPortalProviderId } from './property-source-integration.util';
import { buildPropertyPublicationExternalActions } from './property-publication-links.util';

export const POLLING_INTERVAL_MS = 5000;

export const getOperationLabelKey = (operation: SupportedMeliOperation) =>
  operation === PropertyOperation.SALE
    ? ('catalog.properties.operation.SALE' as const)
    : ('catalog.properties.operation.RENT' as const);

const getSupportedOperation = (
  operations: SupportedMeliOperation[],
  operation: PropertyPortalPublication['operation']
): SupportedMeliOperation | null => {
  const normalizedOperation =
    getSupportedMeliOperationFromPublication(operation);

  return normalizedOperation != null && operations.includes(normalizedOperation)
    ? normalizedOperation
    : null;
};

const getOperationLabel = (params: {
  externalListingId?: string | null;
  operation: PropertyPortalPublication['operation'];
  operations: SupportedMeliOperation[];
  t: BuildPropertyPublicationListItemsParams['t'];
}): string => {
  const supportedOperation = getSupportedOperation(
    params.operations,
    params.operation
  );

  if (supportedOperation != null) {
    return params.t(getOperationLabelKey(supportedOperation));
  }

  if (
    typeof params.externalListingId === 'string' &&
    params.externalListingId.trim().length > 0
  ) {
    return params.externalListingId;
  }

  return params.t('catalog.properties.editor.publishing.publicationGeneric');
};

export const getQueuedDisplayStatus = (params: {
  publication: PropertyPortalPublication | null;
  queuedAction: QueuedPublicationAction | null;
  operation: SupportedMeliOperation;
}): PropertyPortalPublication['status'] | 'queued_publish' | 'unpublished' => {
  const isQueuedPublish =
    params.queuedAction?.action === 'publish' &&
    params.queuedAction.operation === params.operation &&
    params.publication?.status !== PropertyPortalPublicationStatus.PUBLISHED &&
    params.publication?.status !== PropertyPortalPublicationStatus.ERROR;

  return isQueuedPublish
    ? 'queued_publish'
    : getMeliDisplayStatus({ publication: params.publication });
};

export const getOperationDraftParams = (
  property: CatalogProperty,
  operation: SupportedMeliOperation,
  publication: PropertyPortalPublication | null
): PrepareMeliPublicationDraftParams => {
  const latitude = parsePropertyCoordinate(property.latitude);
  const longitude = parsePropertyCoordinate(property.longitude);
  const configuredPictureFileIds = getMeliPublicationConfigStringArray(
    publication,
    'pictureFileIds'
  );
  const pictureFileIds =
    configuredPictureFileIds.length > 0
      ? configuredPictureFileIds
      : getPropertyMediaFileIdsByKinds(property.media, [
          PropertyMediaKind.IMAGE,
        ]);

  return {
    propertyId: property.id,
    operation:
      operation === PropertyOperation.SALE
        ? Extract_PropertyOperation_MeliPublicationDraftOperation_.SALE
        : Extract_PropertyOperation_MeliPublicationDraftOperation_.RENT,
    listingTypeId: getMeliPublicationConfigString(publication, 'listingTypeId'),
    portalTarget: getPublicationPortalTarget(publication),
    pictureFileIds,
    locationHint:
      latitude != null && longitude != null
        ? {
            latitude,
            longitude,
            locality: property.locality ?? null,
            region: property.region ?? null,
            neighborhood: property.neighborhood ?? null,
            addressLine: property.addressDisplay ?? null,
          }
        : null,
  };
};

const isReferenceOnlyPublication = (
  publication: PropertyPortalPublication
): boolean =>
  publication.connectionState ===
    PropertyPortalPublicationConnectionState.REFERENCE_ONLY ||
  publication.recordMode ===
    PropertyPortalPublicationRecordMode.OBSERVED_EXTERNAL;

const getProviderLabel = (params: {
  publication: PropertyPortalPublication;
  t: BuildPropertyPublicationListItemsParams['t'];
}): string => {
  const labelKey = getPropertyPublicationProviderLabelKey(
    params.publication.portal
  );

  if (labelKey) {
    return params.t(labelKey);
  }

  return params.publication.portal;
};

const getProviderBrandId = (
  publication: PropertyPortalPublication
): 'meli' | 'wasi' | 'toppropiedades' | null =>
  getPropertyPublicationProviderBrandId(publication.portal);

const getPortalConnectionState = (
  params: Pick<
    BuildPropertyPublicationListItemsParams,
    'portalConnectionStates'
  >,
  publication: PropertyPortalPublication
): { hasConnection: boolean; isConnected: boolean } => {
  const providerId = resolvePublicationPortalProviderId(publication.portal);

  if (providerId == null) {
    return {
      hasConnection: false,
      isConnected: false,
    };
  }

  return (
    params.portalConnectionStates[publication.portal] ?? {
      hasConnection: false,
      isConnected: false,
    }
  );
};

const getActionDescriptionKey = (
  kind: 'manage' | 'publish' | 'unpublish'
):
  | 'catalog.properties.editor.publishing.actions.manageDescription'
  | 'catalog.properties.editor.publishing.actions.publishDescription'
  | 'catalog.properties.editor.publishing.actions.unpublishDescription' => {
  switch (kind) {
    case 'manage':
      return 'catalog.properties.editor.publishing.actions.manageDescription';
    case 'publish':
      return 'catalog.properties.editor.publishing.actions.publishDescription';
    case 'unpublish':
      return 'catalog.properties.editor.publishing.actions.unpublishDescription';
  }
};

const getInactiveIntegrationTooltip = (params: {
  hasConnection: boolean;
  kind: 'manage' | 'publish' | 'unpublish';
  providerLabel: string;
  t: BuildPropertyPublicationListItemsParams['t'];
}): string => {
  const action = params.t(getActionDescriptionKey(params.kind), {
    provider: params.providerLabel,
  });

  return params.hasConnection
    ? params.t(
        'catalog.properties.editor.publishing.actions.integrationDisconnectedTooltip',
        {
          action,
          provider: params.providerLabel,
        }
      )
    : params.t(
        'catalog.properties.editor.publishing.actions.integrationMissingTooltip',
        {
          action,
          provider: params.providerLabel,
        }
      );
};

const getInactivePropertyPublishTooltip = (
  t: BuildPropertyPublicationListItemsParams['t']
): string =>
  t('catalog.properties.editor.publishing.actions.publishInactiveTooltip');

const getReferenceSourceLabel = (
  publication: PropertyPortalPublication
): string | null => {
  if (
    typeof publication.originProvider === 'string' &&
    publication.originProvider.trim().length > 0
  ) {
    return publication.originProvider.trim();
  }

  return null;
};

const resolveReferenceOnlySummary = (params: {
  publication: PropertyPortalPublication;
  providerLabel: string;
  t: BuildPropertyPublicationListItemsParams['t'];
}): string => {
  const sourceLabel = getReferenceSourceLabel(params.publication);

  if (sourceLabel) {
    return params.t(
      'catalog.properties.editor.publishing.summary.referenceOnlyWithSource',
      {
        provider: params.providerLabel,
        source: sourceLabel,
      }
    );
  }

  return params.t(
    'catalog.properties.editor.publishing.summary.referenceOnly',
    {
      provider: params.providerLabel,
    }
  );
};

const getHistoryToggleLabels = (params: {
  count: number;
  t: BuildPropertyPublicationListItemsParams['t'];
}): Pick<
  PropertyPublicationListItem,
  'historyCollapseLabel' | 'historyExpandLabel'
> => ({
  historyCollapseLabel: params.t(
    'catalog.properties.editor.publishing.history.hideOlderPublications',
    {
      count: `${params.count}`,
    }
  ),
  historyExpandLabel: params.t(
    'catalog.properties.editor.publishing.history.showOlderPublications',
    {
      count: `${params.count}`,
    }
  ),
});

const getManagedPublicationPrimaryActionState = (params: {
  enqueueingPublish: boolean;
  enqueueingUnpublish: boolean;
  handleManagePublication: (
    publicationId: number,
    operation: SupportedMeliOperation | null
  ) => void;
  handleUnpublishShortcut: (
    operation: SupportedMeliOperation,
    publicationId: number
  ) => void;
  isMeliSyncMode: boolean;
  operation: SupportedMeliOperation | null;
  portalConnectionState: {
    hasConnection: boolean;
    isConnected: boolean;
  };
  providerLabel: string;
  publication: PropertyPortalPublication;
  propertyIsActive: boolean;
  t: BuildPropertyPublicationListItemsParams['t'];
}): {
  disabled: boolean;
  kind: 'publish' | 'unpublish';
  label: string;
  onPrimaryAction: () => void;
  tooltipLabel: string;
  variant: ComponentVariant;
} => {
  const {
    enqueueingPublish,
    enqueueingUnpublish,
    handleManagePublication,
    handleUnpublishShortcut,
    isMeliSyncMode,
    operation,
    portalConnectionState,
    providerLabel,
    publication,
    propertyIsActive,
    t,
  } = params;
  const shouldPreferUnpublishShortcut =
    !isMeliSyncMode &&
    !canPublishMeliPublication(publication) &&
    publication.status === PropertyPortalPublicationStatus.PUBLISHED;
  const kind = shouldPreferUnpublishShortcut ? 'unpublish' : 'publish';
  const label =
    kind === 'unpublish'
      ? t('catalog.properties.editor.publishing.actions.unpublish')
      : t('catalog.properties.editor.publishing.actions.publish');

  if (!portalConnectionState.isConnected) {
    return {
      disabled: true,
      kind,
      label,
      onPrimaryAction: () => undefined,
      tooltipLabel: getInactiveIntegrationTooltip({
        hasConnection: portalConnectionState.hasConnection,
        kind,
        providerLabel,
        t,
      }),
      variant: kind === 'unpublish' ? 'danger' : 'warning',
    };
  }

  if (!propertyIsActive && kind === 'publish') {
    return {
      disabled: true,
      kind,
      label,
      onPrimaryAction: () => undefined,
      tooltipLabel: getInactivePropertyPublishTooltip(t),
      variant: 'warning',
    };
  }

  const disabled = shouldPreferUnpublishShortcut
    ? enqueueingUnpublish
    : isMeliSyncMode || enqueueingPublish || operation == null;
  const tooltip =
    kind === 'unpublish'
      ? t('catalog.properties.editor.publishing.actions.unpublishTooltip')
      : isMeliSyncMode
        ? t('catalog.properties.editor.publishing.syncModeDescription')
        : t('catalog.properties.editor.publishing.actions.publishTooltip');
  const variant: ComponentVariant =
    kind === 'unpublish' ? 'danger' : disabled ? 'warning' : 'primary';

  return {
    disabled,
    kind,
    label,
    onPrimaryAction: () => {
      if (kind === 'unpublish') {
        if (enqueueingUnpublish) {
          return;
        }

        if (operation != null) {
          handleUnpublishShortcut(operation, publication.id);
        }
        return;
      }

      handleManagePublication(publication.id, operation);
    },
    tooltipLabel: tooltip,
    variant,
  };
};

const buildPublicationActionItems = (params: {
  externalActions: PropertyPublicationActionItem[];
  manageActionDisabled: boolean;
  manageActionTooltip: string;
  manageActionLabel: string;
  onManage: () => void;
  onPrimaryAction: () => void;
  primaryActionDisabled: boolean;
  primaryActionKind: 'publish' | 'unpublish';
  primaryActionLabel: string;
  primaryActionTooltip: string;
  primaryActionVariant: ComponentVariant;
}): PropertyPublicationActionItem[] => {
  const actions: PropertyPublicationActionItem[] = [];

  actions.push({
    key: 'manage',
    kind: 'manage',
    label: params.manageActionLabel,
    tooltip: params.manageActionTooltip,
    disabled: params.manageActionDisabled,
    onClick: params.onManage,
  });

  actions.push(...params.externalActions);

  actions.push({
    key: params.primaryActionKind,
    kind: params.primaryActionKind,
    label: params.primaryActionLabel,
    tooltip: params.primaryActionTooltip,
    disabled: params.primaryActionDisabled,
    onClick: params.onPrimaryAction,
    variant: params.primaryActionVariant,
    appearance: params.primaryActionDisabled
      ? 'soft'
      : params.primaryActionKind === 'unpublish'
        ? 'ghost'
        : 'soft',
  });

  return actions;
};

const stripHistoryActions = (
  item: PropertyPublicationListItem
): PropertyPublicationListItem => ({
  ...item,
  actions: item.actions.filter(
    (action) => action.kind === 'manage' || action.kind === 'open_external'
  ),
  isHistoryItem: true,
});

const resolveManagedPublicationSummary = (params: {
  isMeliSourcedProperty: boolean;
  isMeliSyncMode: boolean;
  operation: SupportedMeliOperation | null;
  propertyHasAssignee: boolean;
  publication: PropertyPortalPublication;
  queuedAction: QueuedPublicationAction | null;
  t: BuildPropertyPublicationListItemsParams['t'];
}): string => {
  const {
    isMeliSourcedProperty,
    isMeliSyncMode,
    operation,
    propertyHasAssignee,
    publication,
    queuedAction,
    t,
  } = params;

  if (isMeliSyncMode && isMeliSourcedProperty) {
    return propertyHasAssignee
      ? t(
          'catalog.properties.editor.publishing.summary.syncAssignmentResolvedGeneric'
        )
      : t('catalog.properties.editor.publishing.summary.syncAssignmentPending');
  }

  if (
    operation != null &&
    queuedAction?.action === 'publish' &&
    queuedAction.operation === operation
  ) {
    return t('catalog.properties.editor.publishing.summary.pendingPublish');
  }

  if (publication.updatedAt) {
    return t('catalog.properties.editor.publishing.summary.updatedAt', {
      value: formatDate(publication.updatedAt),
    });
  }

  return t('catalog.properties.editor.publishing.notPublishedDescription');
};

const buildManagedPublicationActions = (params: {
  publication: PropertyPortalPublication;
  operation: SupportedMeliOperation | null;
  providerLabel: string;
  portalConnectionState: { hasConnection: boolean; isConnected: boolean };
  canReadPublications: boolean;
  canManagePublications: boolean;
  canDeletePublications: boolean;
  enqueueingPublish: boolean;
  enqueueingUnpublish: boolean;
  handleManagePublication: (
    publicationId: number,
    operation: SupportedMeliOperation | null
  ) => void;
  handleUnpublishShortcut: (
    operation: SupportedMeliOperation,
    publicationId: number
  ) => void;
  isMeliSyncMode: boolean;
  propertyIsActive: boolean;
  t: BuildPropertyPublicationListItemsParams['t'];
}): PropertyPublicationActionItem[] => {
  const externalActions = params.canReadPublications
    ? buildPropertyPublicationExternalActions(params.publication, params.t)
    : [];

  if (!params.canManagePublications) {
    return externalActions;
  }

  const primaryAction = getManagedPublicationPrimaryActionState({
    enqueueingPublish: params.enqueueingPublish,
    enqueueingUnpublish: params.enqueueingUnpublish,
    handleManagePublication: params.handleManagePublication,
    handleUnpublishShortcut: params.handleUnpublishShortcut,
    isMeliSyncMode: params.isMeliSyncMode,
    operation: params.operation,
    portalConnectionState: params.portalConnectionState,
    providerLabel: params.providerLabel,
    publication: params.publication,
    propertyIsActive: params.propertyIsActive,
    t: params.t,
  });
  const manageActionDisabled = !params.portalConnectionState.isConnected;
  const manageActionLabel = params.t(
    'catalog.properties.editor.publishing.actions.manage'
  );
  const manageActionTooltip = manageActionDisabled
    ? getInactiveIntegrationTooltip({
        hasConnection: params.portalConnectionState.hasConnection,
        kind: 'manage',
        providerLabel: params.providerLabel,
        t: params.t,
      })
    : params.t('catalog.properties.editor.publishing.actions.manageTooltip');

  return buildPublicationActionItems({
    externalActions,
    manageActionDisabled,
    manageActionTooltip,
    manageActionLabel,
    onManage: manageActionDisabled
      ? () => undefined
      : () => {
          params.handleManagePublication(
            params.publication.id,
            params.operation
          );
        },
    onPrimaryAction: primaryAction.onPrimaryAction,
    primaryActionDisabled:
      primaryAction.kind === 'unpublish'
        ? primaryAction.disabled || !params.canDeletePublications
        : primaryAction.disabled,
    primaryActionKind: primaryAction.kind,
    primaryActionLabel: primaryAction.label,
    primaryActionTooltip:
      primaryAction.kind === 'unpublish' && !params.canDeletePublications
        ? ''
        : primaryAction.tooltipLabel,
    primaryActionVariant: primaryAction.variant,
  }).filter((action) =>
    action.kind === 'unpublish' ? params.canDeletePublications : true
  );
};

const buildManagedPublicationItem = (
  publication: PropertyPortalPublication,
  params: Omit<
    BuildPropertyPublicationListItemsParams,
    'draftByOperation' | 'loadingDraftByOperation'
  >
): PropertyPublicationListItem => {
  const {
    canDeletePublications,
    canManagePublications,
    canReadPublications,
    enqueueingPublish,
    enqueueingUnpublish,
    handleManagePublication,
    handleUnpublishShortcut,
    isMeliSourcedProperty,
    isMeliSyncMode,
    operations,
    portalConnectionStates,
    propertyIsActive,
    propertyHasAssignee,
    queuedAction,
    t,
  } = params;
  const operation = getSupportedOperation(operations, publication.operation);
  const displayStatus =
    operation != null
      ? getQueuedDisplayStatus({
          publication,
          queuedAction,
          operation,
        })
      : getMeliDisplayStatus({ publication });
  const providerLabel = getProviderLabel({ publication, t });
  const portalConnectionState = getPortalConnectionState(
    { portalConnectionStates },
    publication
  );
  const status = getPropertyPublicationStatusDisplay({
    publication,
    status: displayStatus,
  });
  const publicationActions = buildManagedPublicationActions({
    publication,
    operation,
    providerLabel,
    portalConnectionState,
    canReadPublications,
    canManagePublications,
    canDeletePublications,
    enqueueingPublish,
    enqueueingUnpublish,
    handleManagePublication,
    handleUnpublishShortcut,
    isMeliSyncMode,
    propertyIsActive,
    t,
  });

  return {
    actions: publicationActions,
    key: `publication-${publication.id}`,
    providerId: getProviderBrandId(publication),
    operationLabel: getOperationLabel({
      externalListingId: publication.externalListingId,
      operation: publication.operation,
      operations,
      t,
    }),
    providerLabel,
    status,
    summaryLabel: resolveManagedPublicationSummary({
      isMeliSourcedProperty,
      isMeliSyncMode,
      operation,
      propertyHasAssignee,
      publication,
      queuedAction,
      t,
    }),
  };
};

const buildReferenceOnlyPublicationItem = (
  publication: PropertyPortalPublication,
  params: Pick<
    BuildPropertyPublicationListItemsParams,
    'canReadPublications' | 'operations' | 't'
  >
): PropertyPublicationListItem => {
  const providerLabel = getProviderLabel({
    publication,
    t: params.t,
  });

  return {
    actions: params.canReadPublications
      ? buildPropertyPublicationExternalActions(publication, params.t)
      : [],
    key: `publication-${publication.id}`,
    operationLabel: getOperationLabel({
      externalListingId: publication.externalListingId,
      operation: publication.operation,
      operations: params.operations,
      t: params.t,
    }),
    providerId: getProviderBrandId(publication),
    providerLabel,
    status: getPropertyPublicationStatusDisplay({
      publication,
      status: 'reference_only',
    }),
    summaryLabel: resolveReferenceOnlySummary({
      publication,
      providerLabel,
      t: params.t,
    }),
  };
};

const getDraftPublishActionState = (params: {
  draft:
    | BuildPropertyPublicationListItemsParams['draftByOperation'][SupportedMeliOperation]
    | null;
  enqueueingPublish: boolean;
  loadingDraft: boolean;
  propertyIsActive: boolean;
  t: BuildPropertyPublicationListItemsParams['t'];
}): {
  disabled: boolean;
  tooltip: string;
} => {
  const { draft, enqueueingPublish, loadingDraft, propertyIsActive, t } =
    params;
  const disabled =
    loadingDraft ||
    enqueueingPublish ||
    draft == null ||
    draft.issues.length > 0 ||
    !propertyIsActive;

  if (loadingDraft) {
    return {
      disabled,
      tooltip: t('catalog.properties.meli.actions.validating'),
    };
  }

  if (!propertyIsActive) {
    return {
      disabled,
      tooltip: getInactivePropertyPublishTooltip(t),
    };
  }

  return {
    disabled,
    tooltip:
      draft != null && draft.issues.length === 0
        ? t('catalog.properties.editor.publishing.actions.publishTooltip')
        : '',
  };
};

const buildDraftPublicationItem = (
  operation: SupportedMeliOperation,
  params: Pick<
    BuildPropertyPublicationListItemsParams,
    | 'canManagePublications'
    | 'draftByOperation'
    | 'enqueueingPublish'
    | 'getIssueFieldLabel'
    | 'getIssueMessage'
    | 'handleManage'
    | 'handlePublishShortcut'
    | 'loadingDraftByOperation'
    | 'propertyIsActive'
    | 't'
  >
): PropertyPublicationListItem => {
  const {
    canManagePublications,
    draftByOperation,
    enqueueingPublish,
    getIssueFieldLabel,
    getIssueMessage,
    handleManage,
    handlePublishShortcut,
    loadingDraftByOperation,
    propertyIsActive,
    t,
  } = params;
  const draft = draftByOperation[operation] ?? null;
  const loadingDraft = loadingDraftByOperation[operation] ?? false;
  const firstIssue = draft?.issues[0] ?? null;
  const issueCount = draft?.issues.length ?? 0;
  const firstIssueSummary =
    firstIssue != null
      ? `${getIssueFieldLabel(firstIssue.field)}: ${getIssueMessage(firstIssue)}`
      : '';
  const publishActionState = getDraftPublishActionState({
    draft,
    enqueueingPublish,
    loadingDraft,
    propertyIsActive,
    t,
  });

  return {
    actions: canManagePublications
      ? [
          {
            key: 'manage',
            kind: 'manage',
            label: t('catalog.properties.editor.publishing.actions.manage'),
            tooltip: t(
              'catalog.properties.editor.publishing.actions.manageTooltip'
            ),
            onClick: () => {
              handleManage(operation);
            },
          },
          {
            key: 'publish',
            kind: 'publish',
            label: t('catalog.properties.editor.publishing.actions.publish'),
            tooltipTitle:
              issueCount > 0
                ? t(
                    'catalog.properties.editor.publishing.actions.publishTooltip'
                  )
                : undefined,
            tooltipBody:
              issueCount > 0
                ? [
                    firstIssueSummary,
                    t(
                      'catalog.properties.editor.publishing.actions.publishIssuesCount',
                      {
                        count: `${issueCount}`,
                      }
                    ),
                  ]
                : undefined,
            tooltipFooter:
              issueCount > 0
                ? t(
                    'catalog.properties.editor.publishing.actions.publishIssuesDetailsHint'
                  )
                : undefined,
            tooltipClassName: issueCount > 0 ? 'max-w-sm' : undefined,
            disabled: publishActionState.disabled,
            onClick: () => {
              handlePublishShortcut(operation);
            },
            tooltip: publishActionState.tooltip,
            variant: publishActionState.disabled ? 'warning' : 'primary',
            appearance: 'soft',
          },
        ]
      : [],
    key: `draft-${operation}`,
    operationLabel: t(getOperationLabelKey(operation)),
    providerId: 'meli',
    providerLabel: t('catalog.properties.editor.publishing.target.meli'),
    status: getPropertyPublicationStatusDisplay({ status: 'unpublished' }),
    summaryLabel: t(
      'catalog.properties.editor.publishing.notPublishedDescription'
    ),
  };
};

const buildPublicationGroupItem = (params: {
  historyPublications: PropertyPortalPublication[];
  primaryPublication: PropertyPortalPublication;
  sharedParams: Omit<
    BuildPropertyPublicationListItemsParams,
    'draftByOperation' | 'loadingDraftByOperation'
  >;
}): PropertyPublicationListItem => {
  const baseItem = isReferenceOnlyPublication(params.primaryPublication)
    ? buildReferenceOnlyPublicationItem(params.primaryPublication, {
        canReadPublications: params.sharedParams.canReadPublications,
        operations: params.sharedParams.operations,
        t: params.sharedParams.t,
      })
    : buildManagedPublicationItem(
        params.primaryPublication,
        params.sharedParams
      );

  if (params.historyPublications.length === 0) {
    return baseItem;
  }

  return {
    ...baseItem,
    ...getHistoryToggleLabels({
      count: params.historyPublications.length,
      t: params.sharedParams.t,
    }),
    historyItems: params.historyPublications.map((publication) =>
      stripHistoryActions(
        isReferenceOnlyPublication(publication)
          ? buildReferenceOnlyPublicationItem(publication, {
              canReadPublications: params.sharedParams.canReadPublications,
              operations: params.sharedParams.operations,
              t: params.sharedParams.t,
            })
          : buildManagedPublicationItem(publication, {
              ...params.sharedParams,
              queuedAction: null,
            })
      )
    ),
  };
};

export const buildPropertyPublicationListItems = (
  params: BuildPropertyPublicationListItemsParams
): PropertyPublicationListItem[] => {
  const {
    canDeletePublications,
    canManagePublications,
    canReadPublications,
    draftByOperation,
    enqueueingPublish,
    enqueueingUnpublish,
    getIssueFieldLabel,
    getIssueMessage,
    handleManage,
    handleManagePublication,
    handlePublishShortcut,
    handleUnpublishShortcut,
    isMeliSourcedProperty,
    isMeliSyncMode,
    loadingDraftByOperation,
    operations,
    portalConnectionStates,
    propertyHasAssignee,
    propertyIsActive,
    publications,
    queuedAction,
    t,
  } = params;
  const meliPublications = publications.filter(
    (publication) => publication.portal === PropertyPortal.MELI
  );
  const sharedParams = {
    canDeletePublications,
    canManagePublications,
    canReadPublications,
    enqueueingPublish,
    enqueueingUnpublish,
    getIssueFieldLabel,
    getIssueMessage,
    handleManage,
    handleManagePublication,
    handlePublishShortcut,
    handleUnpublishShortcut,
    isMeliSourcedProperty,
    isMeliSyncMode,
    operations,
    portalConnectionStates,
    propertyIsActive,
    propertyHasAssignee,
    publications,
    queuedAction,
    t,
  } satisfies Omit<
    BuildPropertyPublicationListItemsParams,
    'draftByOperation' | 'loadingDraftByOperation'
  >;
  const publicationItems = operations.flatMap((operation) => {
    const operationPublications = sortMeliPublicationsByRecency(
      meliPublications.filter(
        (publication) =>
          getSupportedOperation(operations, publication.operation) === operation
      )
    );

    if (operationPublications.length === 0) {
      return [];
    }

    const managedPublications = operationPublications.filter(
      (publication) => !isReferenceOnlyPublication(publication)
    );
    const referenceOnlyPublications = operationPublications.filter(
      isReferenceOnlyPublication
    );
    const primaryManagedPublication = getCurrentMeliPublication(
      managedPublications,
      operation
    );

    if (primaryManagedPublication != null) {
      return [
        buildPublicationGroupItem({
          historyPublications: [
            ...managedPublications.filter(
              (publication) => publication.id !== primaryManagedPublication.id
            ),
            ...referenceOnlyPublications,
          ],
          primaryPublication: primaryManagedPublication,
          sharedParams,
        }),
      ];
    }

    const primaryReferencePublication = referenceOnlyPublications.at(0) ?? null;

    if (primaryReferencePublication == null) {
      return [];
    }

    return [
      buildPublicationGroupItem({
        historyPublications: referenceOnlyPublications.filter(
          (publication) => publication.id !== primaryReferencePublication.id
        ),
        primaryPublication: primaryReferencePublication,
        sharedParams,
      }),
    ];
  });

  const draftItems = isMeliSyncMode
    ? []
    : operations
        .filter(
          (operation) =>
            getCurrentMeliPublication(meliPublications, operation) == null
        )
        .map((operation) =>
          buildDraftPublicationItem(operation, {
            draftByOperation,
            canManagePublications,
            enqueueingPublish,
            getIssueFieldLabel,
            getIssueMessage,
            handleManage,
            handlePublishShortcut,
            loadingDraftByOperation,
            propertyIsActive,
            t,
          })
        );

  return [...publicationItems, ...draftItems];
};
